import { useState, useEffect, useRef, useMemo } from "react";
import { composeExpertName } from '@/lib/composeExpertName';
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { db } from "@/lib/supabaseQuery";
import { User, UsersPracticeArea } from "@/types/database";
import { 
  MapPin, 
  Building2,
  ChevronDown,
  ChevronUp,
  Info
} from "lucide-react";
import { ExpertProfileSkeleton } from "@/components/skeletons";
import { getCountryFlag, getCountryName } from "@/lib/flagUtils";
import { ComposeMessageDialog } from '@/components/compose';
import { FavoriteButton } from "@/components/FavoriteButton";
import { useAuth } from "@/contexts/AuthContext";
import { capitalizeWords, decodeHtmlEntities, sanitizeBio as sanitizeBioUtil, truncateAtWord, toAbsoluteUrl } from "@/lib/utils";
import { SEOHead } from "@/components/seo/SEOHead";
import { generatePersonSchema, generateBreadcrumbSchema, generateProfessionalServiceSchema, SPEAKABLE_SELECTORS } from "@/lib/seoSchemas";
import { useLocalizedProfileMetadata } from "@/hooks/useLocalizedProfileMetadata";
import { useProfileSEOMetadata } from "@/hooks/useProfileSEOMetadata";
import { useLocalizedPublicationMetadata } from "@/hooks/useLocalizedPublicationMetadata";

import { useLocalizedRoutes } from "@/hooks/useLocalizedRoutes";
import { useLocale } from "@/contexts/LocaleContext";
import { usePrimaryLocale } from "@/hooks/usePrimaryLocale";
import { useLocalizedPracticeAreas } from "@/hooks/useLocalizedPracticeAreas";
// useTranslation removed - using useLocalizations instead
import { GRIDS, TYPOGRAPHY, SPACING, TABS } from "@/design-system";
import { RESPONSIVE, LAYOUTS } from "@/design-system";
import { ROUTES } from "@/constants";
import { cn } from "@/lib/utils";
import { routeTo } from "@/lib/routeHelpers";
import { useLocalizations } from "@/hooks/useLocalizations";
import { getCountryDisplayName } from "@/hooks/useCountryName";
import i18n from "@/i18n/config";
import { LPages, LCommon } from "@/components/i18n";
import { fetchPublicationPreviews, needsPreview } from "@/hooks/usePublicationPreviews";
import { lazy, Suspense } from "react";
import { Skeleton } from "@/components/ui/skeleton";
const UserLocationMap = lazy(() => import("@/components/UserLocationMap").then(m => ({ default: m.UserLocationMap })));
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import NotFound from "@/pages/NotFound";
import { PublicationCard } from "@/components/PublicationCard";

interface ExpertProfileData extends User {
  practice_areas?: UsersPracticeArea[];
  profile_map_image?: string | null;
}

interface ContentSubmission {
  id: string;
  title: string;
  excerpt: string | null;
  slug: string;
  slug_en: string | null;
  published_at: string | null;
  featured_image_url: string | null;
  source_language: string | null;
  topic_ids: string[] | null;
}

/** Temporary excerpt fallback: fill missing excerpts with a body-derived
 * preview via the get_publication_previews RPC (bodies are not client-readable).
 * Any real excerpt always wins. Remove once the AI excerpt campaign completes. */
async function withExcerptPreviews<T extends { id: string; excerpt: string | null }>(rows: T[]): Promise<T[]> {
  const ids = rows.filter((r) => needsPreview(r.excerpt)).map((r) => r.id);
  if (ids.length === 0) return rows;
  const previews = await fetchPublicationPreviews(ids);
  return rows.map((r) => {
    if (!needsPreview(r.excerpt)) return r;
    const preview = previews.get(r.id);
    return preview ? { ...r, excerpt: preview } : r;
  });
}

const ExpertProfile = () => {
  const { id, vanityUrl, slug } = useParams();
  const { user } = useAuth();
  const { getPath } = useLocalizedRoutes();
  const { lPages, lCommon } = useLocalizations();
  const { formatDate, settings } = useLocale();
  
  const { getDisplayName } = useLocalizedPracticeAreas();
  const { trackProfileView } = useAnalyticsTracking();
  const hasTrackedView = useRef(false);
  const [expert, setExpert] = useState<ExpertProfileData | null>(null);
  const [publications, setPublications] = useState<ContentSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expandedBio, setExpandedBio] = useState(false);
  const [showBioToggle, setShowBioToggle] = useState(false);
  const ARTICLES_PER_PAGE = 6;
  
  // Early profile ID state — set as soon as content_authors resolves,
  // so SEO/bio hooks can start fetching in parallel with remaining queries
  const [profileId, setProfileId] = useState<string | undefined>();
  
  // Fetch pre-translated bio from localization_metadata (metadata-first pattern)
  const { localizedBio, isLoading: bioLoading } = useLocalizedProfileMetadata(profileId);
  
  // Fetch localized publication metadata for published publications
  const publicationIds = useMemo(() => publications.map(a => a.id), [publications]);
  const { getMetadata: getPublicationMetadata } = useLocalizedPublicationMetadata(publicationIds);
  
  // Derive current content language for native-language bypass
  const { resolveToPrimary } = usePrimaryLocale();
  const currentContentLanguage = resolveToPrimary(settings.locale);
  
  // LOCALE-AGNOSTIC: Profile pages show ALL publications regardless of localization status.
  // PublicationCard already handles fallback (localizedTitle || title).
  // Clicking an untranslated publication redirects to its source language at the detail page level.
  const displayablePublications = publications;
  
  // PROFILES: hreflang is identity-level, not translation-level.
  // Always emits all 36 primary locales regardless of translation state.
  // All hreflang URLs point to the same canonical /{vanity_url}.
  // THE CONTRACT (Brief 4): noIndex feeds the robots meta ONLY — the render
  // decision is display_eligible, already folded into localizedBio upstream.
  const { seoMeta, hreflangLinks: profileHreflangLinks, noIndex, canonicalPath } = useProfileSEOMetadata(profileId, expert?.vanity_url);

  // CANONICAL PATH ENFORCEMENT: profiles live at /{vanity_url} — never UUID.
  // If the page was reached via a non-canonical path (e.g. /{uuid}), replace
  // it with the canonical vanity path.
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    if (canonicalPath && pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, pathname, navigate]);

  // Resolve location from location_id (SOURCE OF TRUTH per jurisdictional governance memory).
  // The locations table holds canonical iso_alpha2 + English name for the user's jurisdiction.
  const { data: locationFromId } = useQuery({
    queryKey: ['expert-location', expert?.location_id],
    queryFn: async () => {
      if (!expert?.location_id) return null;
      return await db.fetchOne<{ iso_alpha2: string | null; name: string | null; short_name: string | null }>(
        'locations',
        'iso_alpha2, name, short_name',
        [{ column: 'location_id', operator: 'eq', value: expert.location_id }]
      );
    },
    enabled: !!expert?.location_id,
    staleTime: 10 * 60 * 1000,
  });

  // Track profile view
  useEffect(() => {
    if (expert?.id && !hasTrackedView.current) {
      trackProfileView(expert.id);
      hasTrackedView.current = true;
    }
  }, [expert?.id, trackProfileView]);

  useEffect(() => {
    const fetchExpert = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        // Build query based on whether we have slug (from DeterministicRouter), vanityUrl, or ID
        const vanityUrlParam = slug || vanityUrl;
        
        // Use content_authors view for anonymous-safe access (SECURITY DEFINER view)
        // This enables profile viewing without authentication (public expert pages)
        const { data: authorData, error: authorError } = await db.raw
          .from('content_authors')
          .select('id, display_name, name, lastname, image, vanity_url, country, city, location_id, about, membership_tier')
          .eq(vanityUrlParam ? 'vanity_url' : 'id', vanityUrlParam || id)
          .maybeSingle();

        if (authorError || !authorData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Set profileId early so SEO/bio hooks can start fetching in parallel
        setProfileId(authorData.id);

        // Run all independent queries in parallel (biggest perf win)
        const [userData, roleResult, practiceAreaResult, publicationsData] = await Promise.all([
          // 1. Public profile data via anonymous-safe view (never throws on RLS)
          (async (): Promise<User | null> => {
            const { data } = await db.raw
              .from('users_profiles')
              .select('*')
              .eq('id', authorData.id)
              .maybeSingle();
            return (data as unknown as User | null);
          })(),


          // 2. Check privileged role
          db.raw
            .from('user_roles')
            .select('role')
            .eq('user_id', authorData.id)
            .in('role', ['super_admin', 'admin', 'engineer'])
            .limit(1)
            .maybeSingle(),
          // 3. Practice areas (nested join)
          db.raw
            .from('users_users_practice_areas')
            .select(`
              practice_area_id,
              users_practice_areas (
                practice_area_id,
                practice_area
              )
            `)
            .eq('user_id', authorData.id),
          // 4. Published publications
          db.fetchTop<ContentSubmission>(
            'content_submissions',
            'id, title, excerpt, slug, slug_en, published_at, featured_image_url, source_language, topic_ids',
            ARTICLES_PER_PAGE,
            {
              filters: [
                { column: 'author_id', operator: 'eq', value: authorData.id },
                { column: 'editorial_status', operator: 'eq', value: 'published' }
              ],
              orderBy: { column: 'published_at', ascending: false }
            }
          )
        ]);

        // Check privileged role - hide profile
        if (roleResult.data) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Use authorData as fallback if users table query fails (RLS restriction for anonymous)
        const userDataResolved = userData || {
          id: authorData.id,
          display_name: authorData.display_name,
          name: authorData.name,
          lastname: authorData.lastname,
          image: authorData.image,
          vanity_url: authorData.vanity_url,
          country: authorData.country,
          city: authorData.city,
          location_id: authorData.location_id,
          about: authorData.about,
          membership_tier: (authorData as Record<string, unknown>).membership_tier as string | null,
        } as User;

        // Transform practice areas data - handle nested join structure
        interface PracticeAreaJoin {
          practice_area_id: string;
          users_practice_areas: { practice_area_id: string; practice_area: string }[] | { practice_area_id: string; practice_area: string } | null;
        }
        const practiceAreas = (practiceAreaResult.data as PracticeAreaJoin[] | null)?.map(pa => {
          const paData = Array.isArray(pa.users_practice_areas) 
            ? pa.users_practice_areas[0] 
            : pa.users_practice_areas;
          return {
            practice_area_id: pa.practice_area_id,
            practice_area: paData?.practice_area || null
          };
        }) || [];

        setExpert({
          ...userDataResolved,
          practice_areas: practiceAreas
        });

        setPublications(await withExcerptPreviews(publicationsData || []));
        setHasMore((publicationsData?.length || 0) >= ARTICLES_PER_PAGE);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchExpert();
  }, [id, vanityUrl, slug]);

  const loadMorePublications = async () => {
    if (!expert || loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      // .range() pagination requires db.raw
      const { data: morePublications, error } = await db.raw
        .from('content_submissions')
        .select('id, title, excerpt, slug, slug_en, published_at, featured_image_url, source_language, topic_ids')
        .eq('author_id', expert.id)
        .eq('editorial_status', 'published')
        .order('published_at', { ascending: false })
        .range(publications.length, publications.length + ARTICLES_PER_PAGE - 1);

      if (error) {
        // Loading more failed - keep existing publications
      } else if (morePublications) {
        const withPreviews = await withExcerptPreviews(morePublications);
        setPublications([...publications, ...withPreviews]);
        setHasMore(morePublications.length >= ARTICLES_PER_PAGE);
      }
    } catch {
      // Silent fail for load more
    } finally {
      setLoadingMore(false);
    }
  };


  const normalizeCity = (city: string | null) => {
    if (!city) return null;
    // Trim and normalize multiple spaces to single space
    return city.trim().replace(/\s+/g, ' ');
  };

  // Show skeleton during data fetch (router-rendered page, no Suspense)
  if (loading) {
    return <ExpertProfileSkeleton />;
  }

  if (notFound || !expert) {
    return <NotFound entityType="expert" />;
  }

  const rawExpertName = composeExpertName(expert.display_name, expert.lastname, expert.name) || expert.business || lCommon('labels.expert', { defaultValue: 'Expert' });
  const expertName = capitalizeWords(decodeHtmlEntities(rawExpertName));
  const normalizedCity = normalizeCity(expert.city);

  // location_id is the SOURCE OF TRUTH for jurisdictional data (per project memory).
  // Legacy users.country is deprecated and only used as last-resort fallback.
  const resolvedCountryCode = locationFromId?.iso_alpha2 || expert.country || null;
  // Localized country name in the visitor's language (e.g. "Brasil" on pt pages)
  const resolvedCountryName = (resolvedCountryCode ? getCountryDisplayName(resolvedCountryCode, i18n.language) : null) || locationFromId?.short_name || locationFromId?.name || getCountryName(expert.country) || null;
  const countryFlag = getCountryFlag(resolvedCountryCode);
  const countryName = resolvedCountryName;
  // LOCALE-AGNOSTIC: Profile is an identity page - always show bio.
  // Prefer localized bio, fallback to original bio if translation is missing.
  const displayBio = localizedBio ?? expert.about ?? null;
  const sanitizedBio = displayBio ? sanitizeBioUtil(displayBio) : '';
  // Canonical profile URL: flat /{vanity_url} (identity invariant - never /experts/ prefix)
  const profileUrl = `${window.location.origin}/${expert?.vanity_url || expert?.id}`;
  const bio = displayBio ? decodeHtmlEntities(sanitizeBioUtil(displayBio)) : '';

  // Generate structured data schemas
  const personSchema = generatePersonSchema({
    name: expertName,
    jobTitle: expert.business ? decodeHtmlEntities(expert.business) : undefined,
    description: seoMeta?.schema_about || seoMeta?.aio_snippet || truncateAtWord(bio, 300),
    image: toAbsoluteUrl(expert.image) || undefined,
    url: profileUrl,
    address: {
      city: normalizedCity || undefined,
      country: countryName || undefined,
    },
    worksFor: expert.business ? decodeHtmlEntities(expert.business) : undefined,
    speakable: {
      cssSelectors: [
        SPEAKABLE_SELECTORS.expert.name,
        SPEAKABLE_SELECTORS.expert.title,
        SPEAKABLE_SELECTORS.expert.bio,
      ]
    }
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: window.location.origin },
    { name: lPages('navigation.experts', { defaultValue: 'Experts' }), url: `${window.location.origin}/experts` },
    { name: expertName, url: profileUrl }
  ]);

  const professionalServiceSchema = generateProfessionalServiceSchema({
    name: `${expertName} - Tax Services`,
    description: seoMeta?.meta_description || truncateAtWord(bio, 200) || `Tax services by ${expertName}`,
    url: profileUrl,
    provider: {
      name: expertName,
      image: toAbsoluteUrl(expert.image) || undefined,
      address: {
        city: normalizedCity || undefined,
        country: countryName || undefined,
      }
    },
    areaServed: countryName || undefined
  });

  // Combine all schemas
  const schemas = [personSchema, breadcrumbSchema, professionalServiceSchema];

  return (
    <>
      <SEOHead
        title={seoMeta?.meta_title ?? expertName}
        description={seoMeta?.meta_description ?? (truncateAtWord(bio, 160) || undefined)}
        type="profile"
        author={expertName}
        image={toAbsoluteUrl(expert.image) || undefined}
        url={profileUrl}
        canonicalUrl={profileUrl}
        schema={schemas}
        hreflangLinks={profileHreflangLinks}
        noindex={noIndex}
        twitterCardOverride="summary"
        profileFirstName={expert.name || undefined}
        profileLastName={expert.lastname || undefined}
        locale={(settings.locale || settings.language).replace('-', '_')}
      />
      <main className={cn("container", RESPONSIVE.pageContainerPy)}>
        <div className={LAYOUTS.sidebarLayout}>
          {/* Main Profile Section */}
          <div className={LAYOUTS.sidebarMain}>
            <Card>
              <CardHeader>
                <div className={RESPONSIVE.profileHeaderLayout}>
                  {/* Profile Image */}
                  <div id="main-profile-image" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                    {expert.image ? (
                      <img 
                        src={expert.image} 
                        alt={expertName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className={cn(TYPOGRAPHY.metricValueXL, 'text-muted-foreground')}>
                        {expertName[0].toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Name and Title */}
                  <div className={cn("flex-1", RESPONSIVE.textAlignCenterToLeft)}>
                    <div className={cn(RESPONSIVE.nameRowWithBadge, "mb-2")}>
                      <CardTitle id="expert-name" className={RESPONSIVE.titleLg}>{expertName}</CardTitle>
                    {expert.membership_tier != null && (
                        <VerifiedBadge size={24} displayName={expertName} membershipTier={expert.membership_tier} />
                      )}
                      {user && expert.id !== user.id && (
                      <FavoriteButton 
                          userId={expert.id} 
                          variant="ghost" 
                          size="icon"
                          className="ml-auto"
                        />
                      )}
                    </div>
                    
                    {expert.business && (
                      <p id="expert-title" className={cn(RESPONSIVE.textResponsiveLg, "text-muted-foreground flex items-center justify-center sm:justify-start gap-2 mb-2")}>
                        <Building2 className="h-4 w-4" />
                        {decodeHtmlEntities(expert.business)}
                      </p>
                    )}
                    
                    {(expert.location_id || normalizedCity || countryFlag || countryName) && (
                      <p className="text-muted-foreground flex items-center justify-center sm:justify-start gap-2">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        {normalizedCity && (
                          <span>{normalizedCity}{(countryFlag || countryName) && ','}</span>
                        )}
                        {countryFlag && (
                          <span className="text-xl leading-none">{countryFlag}</span>
                        )}
                        {countryName && <span>{countryName}</span>}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>

              {sanitizedBio && (
                <CardContent className="pb-6">
                  <Separator className={cn(SPACING.sectionHeader)} />
                  <div className="relative">
                    <div 
                      id="expert-bio-summary"
                      ref={(el) => {
                        if (el && !expandedBio) {
                          setShowBioToggle(el.scrollHeight > el.clientHeight);
                        }
                      }}
                      className={cn(
                        "text-muted-foreground whitespace-pre-wrap leading-relaxed transition-all duration-300 ease-in-out",
                        !expandedBio && "line-clamp-4"
                      )}
                    >
                      {sanitizedBio}
                    </div>
                    
                    {showBioToggle && !expandedBio && (
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
                    )}
                  </div>
                  
                  {showBioToggle && (
                    <button
                      onClick={() => setExpandedBio(!expandedBio)}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 hover:underline mt-2 transition-all"
                      aria-expanded={expandedBio}
                      aria-label={expandedBio
                        ? lPages('expert_profile.bio.show_less_bio', { defaultValue: 'Show less of the bio' })
                        : lPages('expert_profile.bio.read_full_bio', { defaultValue: 'Read full bio' })}
                      aria-controls="expert-bio-summary"
                    >
                      <LPages 
                        id={expandedBio ? "expert_profile.bio.read_less" : "expert_profile.bio.read_more"} 
                        defaultValue={expandedBio ? "Read less" : "Read more"} 
                      />
                      {expandedBio ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Practice Areas */}
            {expert.practice_areas && expert.practice_areas.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle><LPages id="expert_profile.practice_areas.title" defaultValue='Practice Areas' /></CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={GRIDS.tagWrap}>
                    {expert.practice_areas.map((area, index) => (
                      <Badge key={area.practice_area_id || index} variant="outline">
                        {getDisplayName(area.practice_area)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Published Publications */}
            {displayablePublications.length > 0 && (
              <div className="space-y-4">
                {displayablePublications.map((publication) => {
                  const publicationMeta = getPublicationMetadata(publication.id);
                  return (
                    <PublicationCard
                      key={publication.id}
                      id={publication.id}
                      slug={publication.slug}
                      slugEn={publication.slug_en ?? undefined}
                      title={publication.title}
                      excerpt={publication.excerpt ?? ''}
                      localizedTitle={publicationMeta?.title}
                      localizedExcerpt={publicationMeta?.excerpt}
                      localizedSlug={publicationMeta?.slug}
                      featuredImage={publication.featured_image_url || undefined}
                      topicIds={publication.topic_ids ?? undefined}
                      authorId={expert.id}
                      authorName={expert.display_name || expert.name || ''}
                      authorVanityUrl={expert.vanity_url || undefined}
                      publishedAt={publication.published_at || undefined}
                      variant="horizontal"
                      hideAuthorInfo
                    />
                  );
                })}
                
                {hasMore && (
                  <Button 
                    variant="outline" 
                    className={cn(TABS.contentNested, 'w-full')}
                    onClick={loadMorePublications}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <LCommon
                      id="status.loading_description"
                      defaultValue='The system is currently loading data.' /> : <LCommon id="actions.load_more" defaultValue='Load More' />}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Contact Expert */}
          <div className={cn(SPACING.pageContainer)}>
            {/* Own Profile Info Banner */}
            {user && user.id === expert.id && (
              <Card className="border-primary/50 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div className={cn(SPACING.listItemGap)}>
                      <p className="text-sm">
                        <LPages id="expert_profile.own_profile.banner" defaultValue='Your Expert Profile' />
                      </p>
                      <Button asChild variant="outline" size="sm">
                        <Link to={ROUTES.ACCOUNT_PUBLICATIONS_NEW}>
                          <LPages id="expert_profile.own_profile.publish_cta" defaultValue='Publish Profile' />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Card - Sticky */}
            <div className="lg:sticky lg:top-24">
              <Card>
                <CardHeader>
                  <CardTitle><LPages id="expert_profile.contact.title" defaultValue='Contact' /></CardTitle>
                  <CardDescription>
                    <LPages id="expert_profile.contact.description" defaultValue='Send a private message to this expert' />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="w-full">
                    <ComposeMessageDialog
                      initialRecipients={[{ 
                        id: expert.id, 
                        name: expertName, 
                        image: expert.image || undefined 
                      }]}
                      lockedRecipients={true}
                      context="frontend"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>


            {/* Location Map */}
            {(expert.location_id || expert.city || expert.country) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base"><LPages id="expert_profile.location.title" defaultValue='Location' /></CardTitle>
                </CardHeader>
                <CardContent>
                  <Suspense fallback={<Skeleton className="h-48 w-full rounded-lg" />}>
                    <UserLocationMap
                      countryCode={resolvedCountryCode}
                      city={normalizedCity}
                      userName={expertName}
                      userId={expert.id}
                      profileMapImage={expert.profile_map_image || undefined}
                    />
                  </Suspense>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default ExpertProfile;
